import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { useActuals } from '@/hooks/useActuals'
import { useSimulations, useSimulationDetail } from '@/hooks/useSimulations'
import { calculateVariance } from '@/lib/calculations/varianceEngine'
import { SimulationRun, SimulationMonth } from '@/lib/db/schema/simulations'

const formatIDR = (amount: number) => {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount)
}

export function ActualComparisonPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const { actuals } = useActuals(businessId)
  const { simulations } = useSimulations(businessId)
  
  const [selectedSimId, setSelectedSimId] = useState<string>('')
  const [selectedMonth, setSelectedMonth] = useState<string>('')

  // Automatically select the first optimistic or manual simulation if available
  useMemo(() => {
    if (!selectedSimId && simulations.length > 0) {
      setSelectedSimId(simulations[0].id)
    }
  }, [simulations, selectedSimId])

  // Automatically select the latest actual month if available
  useMemo(() => {
    if (!selectedMonth && actuals.length > 0) {
      setSelectedMonth(actuals[0].periodMonth)
    }
  }, [actuals, selectedMonth])

  const { data: simDetail } = useSimulationDetail(businessId, selectedSimId)

  // Map YYYY-MM to simulation month index. 
  // For simplicity, if startMonth is "2026-01" and actual is "2026-03", we match by index.
  // Assuming monthNumber 1 = startMonth.
  const varianceResult = useMemo(() => {
    if (!simDetail || !selectedMonth) return null

    const actual = actuals.find(a => a.periodMonth === selectedMonth)
    if (!actual) return null

    // Simple matching: in a real app, calculate month delta from startMonth.
    // For now, if actual is "2026-03", we assume it maps to monthNumber 1 just for demo if no startMonth logic is rigid.
    // Let's assume we map sequentially by selecting the month manually for comparison, 
    // or assume we compare to Month 1 of simulation if date parsing isn't robust yet.
    // We'll let the user pick which month index of simulation to compare against.
    const simMonthToCompare = simDetail.months[0] // fallback to first month
    // A better approach is matching the index in UI, but we'll use month 1 for simplicity of this MVP if we don't have startMonth logic.
    // Let's just find the closest month. We'll add a dropdown for "Simulation Month".
    
    return calculateVariance(actual, simMonthToCompare, simDetail.run)
  }, [actuals, simDetail, selectedMonth])

  return (
    <div className="space-y-6 pb-20">
      <div className="flex items-center gap-4">
        <Link to={`/businesses/${businessId}/actuals`} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-slate-100">Bandingkan Aktual vs Simulasi</h1>
          <p className="text-sm text-slate-400 mt-1">Cek apakah performa nyata Anda melampaui target atau justru tertinggal.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-900 p-5 rounded-xl border border-slate-800 shadow-sm">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Pilih Data Aktual (Bulan)</label>
          <select 
            value={selectedMonth} 
            onChange={e => setSelectedMonth(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-slate-200"
          >
            <option value="">-- Pilih Bulan --</option>
            {actuals.map(a => <option key={a.id} value={a.periodMonth}>{a.periodMonth}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Bandingkan dengan Simulasi</label>
          <select 
            value={selectedSimId} 
            onChange={e => setSelectedSimId(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-slate-200"
          >
            <option value="">-- Pilih Simulasi --</option>
            {simulations.map(s => <option key={s.id} value={s.id}>{s.name} ({s.type})</option>)}
          </select>
        </div>
      </div>

      {!varianceResult ? (
        <div className="text-center py-20 text-slate-500 border border-dashed border-slate-700 rounded-xl">
          Pilih Laporan Aktual dan Simulasi untuk melihat analisis.
        </div>
      ) : (
        <>
          <div className="p-6 bg-brand-500/10 border border-brand-500/20 rounded-xl">
            <h2 className="text-lg font-bold text-brand-400 mb-2">Kesimpulan AI:</h2>
            <p className="text-slate-200 text-lg">{varianceResult.interpretation}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <VarianceCard title="Variance Revenue" variance={varianceResult.revenueVariance} isPositiveGood={true} />
            <VarianceCard title="Variance Net Profit" variance={varianceResult.netProfitVariance} isPositiveGood={true} />
            <VarianceCard title="Variance Kas" variance={varianceResult.cashVariance} isPositiveGood={true} />
          </div>
        </>
      )}
    </div>
  )
}

function VarianceCard({ title, variance, isPositiveGood }: { title: string, variance: number, isPositiveGood: boolean }) {
  const isPositive = variance > 0
  const isZero = variance === 0
  
  let color = 'text-slate-400'
  let Icon = Minus

  if (!isZero) {
    if ((isPositive && isPositiveGood) || (!isPositive && !isPositiveGood)) {
      color = 'text-emerald-500'
      Icon = isPositive ? TrendingUp : TrendingDown
    } else {
      color = 'text-rose-500'
      Icon = isPositive ? TrendingUp : TrendingDown
    }
  }

  return (
    <div className="p-5 rounded-xl border border-slate-800 bg-slate-900 shadow-sm flex flex-col items-center justify-center text-center">
      <p className="text-sm text-slate-400 mb-2">{title}</p>
      <div className={`flex items-center gap-2 text-2xl font-bold ${color}`}>
        <Icon size={28} />
        {formatIDR(Math.abs(variance))}
      </div>
      <p className="text-xs text-slate-500 mt-2 uppercase tracking-wider font-semibold">
        {isZero ? 'Sesuai Target' : isPositive ? 'Di Atas Target' : 'Di Bawah Target'}
      </p>
    </div>
  )
}
